import { useCallback, useEffect, useState } from 'react';
import { getActiveInitiativeId, setActiveInitiativeId } from '../config';
import { apiFetch } from '../api/client';
import { isPitchModeActive } from './usePitchMode';

// In pitch/demo mode a fresh browser has no stored initiative, which used to
// leave every initiative-scoped dashboard (CFIR/ERIC etc.) empty until the
// presenter manually picked one — a recurring demo failure. Prefer an
// initiative whose name is explicitly marked as demo material; fall back to
// the first one so scoped requests always succeed.
function pickPitchDefault(list: Initiative[]): Initiative | null {
  if (list.length === 0) return null;
  return list.find((i) => /demo/i.test(pickName(i))) || list[0];
}

export interface Initiative {
  id: number;
  initiative_name?: string;
  project_name?: string;
  name?: string;
  intervention_vertical?: string;
}

function pickName(i: Initiative): string {
  return (i.initiative_name || i.project_name || i.name || `Initiative #${i.id}`).trim();
}

export function useActiveInitiative() {
  const [initiatives, setInitiatives] = useState<Initiative[]>([]);
  const [activeId, setActiveIdState] = useState<string | null>(getActiveInitiativeId());
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      // NOTE: no trailing slash — the backend route is defined slash-less and
      // newer Starlette no longer redirects '/api/project-configs/' (404),
      // which silently emptied the initiative switcher in every browser.
      const res = await apiFetch('/api/project-configs', { context: 'admin' });
      if (!res.ok) {
        setInitiatives([]);
        return;
      }
      const data = await res.json();
      const list: Initiative[] = Array.isArray(data) ? data : (data.items || data.configs || []);
      setInitiatives(list);
      // Auto-select when nothing is stored: always when there's exactly one
      // initiative; in pitch/demo mode also when there are several (prefer a
      // demo-named one) so a fresh browser never opens to empty dashboards.
      if (!getActiveInitiativeId() && list.length > 0) {
        const pick = list.length === 1
          ? list[0]
          : (isPitchModeActive() ? pickPitchDefault(list) : null);
        if (pick) {
          setActiveInitiativeId(pick.id);
          setActiveIdState(String(pick.id));
        }
      }
    } catch {
      setInitiatives([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const onChange = () => setActiveIdState(getActiveInitiativeId());
    window.addEventListener('spartis:activeInitiativeChanged', onChange);
    window.addEventListener('storage', onChange);
    return () => {
      window.removeEventListener('spartis:activeInitiativeChanged', onChange);
      window.removeEventListener('storage', onChange);
    };
  }, [refresh]);

  const setActive = useCallback((id: string | number | null) => {
    setActiveInitiativeId(id);
    setActiveIdState(id === null ? null : String(id));
  }, []);

  const active = initiatives.find((i) => String(i.id) === activeId) || null;
  return { initiatives, activeId, active, setActive, loading, pickName, refresh };
}
